<template>
	<b-form-group class="mb-3">
		<slot name="label">
			<legend class="bv-no-focus-ring col-form-label pt-0">
				{{ $t(field.label) }}
				<span v-if="field.indexReferenceNumber">
					<small> [{{ field.indexReferenceNumber }}] </small>
				</span>
				<a
					v-if="field.labelHyperlink"
					:href="getExternalUrl(field.labelHyperlink.url)"
					target="_blank"
				>
					{{ field.labelHyperlink.text }} </a
				>&nbsp;
				<b-form-text v-if="field.required" class="mt-0" style="display: inline-block;">{{
					$t("required")
				}}</b-form-text>
			</legend>
		</slot>
		<template v-if="fieldTypes[field.fieldType]">
			<component
				:field="field"
				v-if="field.fieldType != 'file' || (field.fieldType == 'file' && files.length <= 0)"
				:is="fieldConfig.is"
				v-bind="fieldConfig"
				:options="options"
				:state="state"
				:value="value"
				@input="updateValue"
				:reduce="o => o.code"
			></component>

			<template v-if="field.fieldType == 'file'">
				<template v-if="files.length > 0">
					{{ $t("attachedFile") }}: {{ value ? value.name : "" }}
				</template>

				<template v-else-if="Boolean(value)">
					{{ $t("chosenFile") }}: {{ value ? value.name : "" }}
				</template>

				<b-row class="mt-2">
					<b-col>
						<b-button
							variant="primary"
							v-if="Boolean(value) && files.length <= 0"
							class="mr-2"
							@click="uploadFile(field, value)"
						>
							<template v-if="!uploading"> {{ $t("save") }} {{ field.label }} </template>
							<template v-else>
								<b-spinner small></b-spinner>
								{{ progress }}%
							</template>
						</b-button>
						<b-button
							variant="outline-danger"
							v-if="files.length > 0"
							class="mr-2"
							@click="removeFile(field, value)"
						>
							{{ $t("delete") }} {{ field.label }}</b-button
						>
					</b-col>
				</b-row>
				<b-alert v-if="error" class="mt-2" show variant="danger">{{ error }}</b-alert>
			</template>

			<template v-if="field.helpTexts && field.helpTexts.length && field.helpTexts.length >= 1">
				<b-form-text v-for="text in field.helpTexts" :key="text">
					<template v-if="text == 'Hyväksyn että tietoni tallennetaan'">
						{{ $t(text, { orgName: orgName }) }}
					</template>
					<template v-else>
						{{ $t(text) }}
					</template>
				</b-form-text>
			</template>

			<template v-if="state === false">
				<div class="invalid-feedback" style="display:block;" v-for="msg in errors" :key="msg">
					{{ msg }}
				</div>
			</template>
		</template>
	</b-form-group>
</template>

<script>
import axios from "axios";

import DatePicker from "@/components/Fields/DatePicker.vue";
import File from "@/components/Fields/File.vue";
import TextInput from "@/components/Fields/TextInput.vue";
import NumberInput from "@/components/Fields/NumberInput.vue";
import Treeselect from "@/components/Fields/Treeselect.vue";
import VSelect from "vue-select";

import "vue-select/dist/vue-select.css";

export default {
	props: ["value", "field", "subFields", "state", "errors", "files", "orgName"],
	components: {
		DatePicker,
		File,
		TextInput,
		NumberInput,
		Treeselect,
		VSelect
	},
	computed: {
		fieldConfig() {
			let cfg = { ...this.fieldTypes[this.field.fieldType] };

			if (this.field.fieldType == "radio") {
				if (this.field.buttons) {
					cfg.buttons = true;
					cfg.buttonVariant = this.buttonRadioStyle(this.state);
				}
			}

			if (this.field.fieldType == "checkbox") {
				if (this.field.stacked) {
					cfg.stacked = true;
				}
			}

			return cfg;
		},
		options() {
			let lang = this.$route.params.lang;
			if (this.field.fieldType == "treeselect") {
				return this.field.options;
			}

			if (!this.field.options) {
				return null;
			}

			if (this.field.fieldType == "select") {
				return this.field.options
					.filter(o => !o.disabled)
					.map(o => {
						if (lang == "se" && o.labelSe) {
							return {
								code: o.id,
								label: o.labelSe,
								disabled: o.disabled,
								value: o.id,
								text: o.labelSe
							};
						} else if (lang == "en" && o.labelEn) {
							return {
								code: o.id,
								label: o.labelEn,
								disabled: o.disabled,
								value: o.id,
								text: o.labelEn
							};
						} else {
							return {
								code: o.id,
								label: o.label,
								disabled: o.disabled,
								value: o.id,
								text: o.label
							};
						}
					});
			}

			return this.field.options.map(o => {
				if (lang == "se" && o.labelSe) {
					return {
						value: o.id,
						text: o.labelSe,
						disabled: o.disabled,
						html: o.indexReferenceNumber
							? `${o.labelSe} <small>[${o.indexReferenceNumber}]</small>`
							: undefined
					};
				} else if (lang == "en" && o.labelEn) {
					return {
						value: o.id,
						text: o.labelEn,
						disabled: o.disabled,
						html: o.indexReferenceNumber
							? `${o.labelEn} <small>[${o.indexReferenceNumber}]</small>`
							: undefined
					};
				} else {
					return {
						value: o.id,
						text: o.label,
						disabled: o.disabled,
						html: o.indexReferenceNumber
							? `${o.label} <small>[${o.indexReferenceNumber}]</small>`
							: undefined
					};
				}
			});
		}
	},
	methods: {
		updateValue(e) {
			//console.log("e :>> ", e);
			this.$emit("input", e);
		},
		buttonRadioStyle(state) {
			let styles = {
				true: "outline-success",
				false: "outline-danger",
				null: "outline-secondary"
			};

			return styles[state];
		},
		async uploadFile(field, file) {
			var fileSizeMb = file.size / 1024.0 / 1024.0;

			if (fileSizeMb > 50) {
				this.error = "Tiedosto on liian suuri";
				return;
			}
			this.uploading = true;

			let formData = new FormData();

			formData.append("file", file);

			let response;
			let fileId;

			try {
				response = await axios.post("uploadDocument", formData, {
					onUploadProgress: progressEvent => {
						const totalLength = progressEvent.lengthComputable
							? progressEvent.total
							: progressEvent.target.getResponseHeader("content-length") ||
							  progressEvent.target.getResponseHeader("x-decompressed-content-length");
						console.log("onUploadProgress", totalLength);
						if (totalLength !== null) {
							this.progress = Math.round((progressEvent.loaded * 100) / totalLength);
						}
					}
				});
				fileId = response.data;
				this.$emit("uploadFile", { field, fileId });
			} catch (error) {
				if (typeof error.response.data === "string") {
					this.error = error.response.data;
				}
				this.removeFile(field, file);
			}

			this.uploading = false;
		},
		removeFile(field, file) {
			this.$emit("removeFile", { field, file });
		},
		getExternalUrl(url) {
			if (!url) {
				return "";
			}
			let split = url.split("://");
			if (split.length > 1) {
				return "http://" + split[1];
			} else {
				return "http://" + split[0];
			}
		}
	},
	data() {
		return {
			uploading: false,
			error: null,
			progress: 0,
			fieldTypes: {
				textarea: { is: "b-form-textarea" },
				text: { is: "TextInput" },
				number: { is: "NumberInput" },
				select: { is: "VSelect" },
				//select: { is: "b-form-select" },
				//date: { is: "b-form-datepicker", type: null },
				date: { is: "DatePicker" },
				fieldgroup: { is: "b-form-group" },
				radio: { is: "b-form-radio-group" },
				checkbox: { is: "b-form-checkbox-group" },
				file: { is: "File" },
				treeselect: { is: "Treeselect" }
			}
		};
	}
};
</script>

<style lang="scss" scoped></style>
